import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Paper, CircularProgress, Alert,
    List, ListItem, ListItemText, ListItemSecondaryAction,
    IconButton, Select, MenuItem, Button, FormControl, InputLabel
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { getAllUsers, getObraUsers, assignUserToObra, removeUserFromObra } from '../../services/obraService';

interface ObraUsuariosTabProps {
    obraId: number;
}

export const ObraUsuariosTab: React.FC<ObraUsuariosTabProps> = ({ obraId }) => {
    const [allUsers, setAllUsers] = useState<any[]>([]);
    const [obraUsers, setObraUsers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedUser, setSelectedUser] = useState<string>('');
    const [assigning, setAssigning] = useState(false);

    const fetchData = React.useCallback(async () => {
        try {
            setLoading(true);
            const [usersResponse, obraUsersResponse] = await Promise.all([
                getAllUsers(),
                getObraUsers(obraId)
            ]);
            setAllUsers(usersResponse || []);
            setObraUsers(obraUsersResponse || []);
        } catch (err: any) {
            console.error('Error fetching users:', err);
            setError(err.message || 'Erro ao carregar usuários.');
        } finally {
            setLoading(false);
        }
    }, [obraId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleAssignUser = async () => {
        if (!selectedUser) return;
        try {
            setAssigning(true);
            await assignUserToObra(obraId, selectedUser);
            setSelectedUser('');
            await fetchData();
        } catch (err: any) {
            console.error('Error assigning user:', err);
            setError(err.message || 'Erro ao atribuir usuário.');
        } finally {
            setAssigning(false);
        }
    };

    const handleRemoveUser = async (userId: string) => {
        if (!window.confirm('Tem certeza que deseja remover este usuário da obra?')) return;
        try {
            await removeUserFromObra(obraId, userId);
            await fetchData();
        } catch (err: any) {
            console.error('Error removing user:', err);
            setError(err.message || 'Erro ao remover usuário.');
        }
    };

    if (loading) return <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
    if (error) return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;

    // Filter out users that are already assigned
    const assignedUserIds = new Set(obraUsers.map((ou: any) => ou.user_id));
    const availableUsers = allUsers.filter((u: any) => !assignedUserIds.has(u.id));

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
                Gerenciamento de Usuários Responsáveis
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
                Atribua usuários a esta obra para que eles possam visualizar seus detalhes, lançamentos e anexos.
                Administradores têm acesso a todas as obras por padrão.
            </Typography>

            <Paper sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="select-user-label">Selecione um usuário</InputLabel>
                        <Select
                            labelId="select-user-label"
                            value={selectedUser}
                            label="Selecione um usuário"
                            onChange={(e) => setSelectedUser(e.target.value)}
                        >
                            {availableUsers.map(u => (
                                <MenuItem key={u.id} value={u.id}>
                                    {u.raw_user_meta_data?.name || u.email} ({u.email})
                                </MenuItem>
                            ))}
                            {availableUsers.length === 0 && (
                                <MenuItem disabled value="">Nenhum usuário disponível para atribuir.</MenuItem>
                            )}
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAssignUser}
                        disabled={!selectedUser || assigning}
                    >
                        {assigning ? 'Atribuindo...' : 'Atribuir'}
                    </Button>
                </Box>
            </Paper>

            <Paper>
                <List>
                    {obraUsers.length === 0 ? (
                        <ListItem>
                            <ListItemText primary="Nenhum usuário atribuído a esta obra." />
                        </ListItem>
                    ) : (
                        obraUsers.map((ou: any) => {
                            const userInfo = allUsers.find(u => u.id === ou.user_id);
                            const name = userInfo?.raw_user_meta_data?.name || 'Usuário Desconhecido';
                            const email = userInfo?.email || '';

                            return (
                                <ListItem key={ou.user_id} divider>
                                    <ListItemText
                                        primary={name}
                                        secondary={`E-mail: ${email} | Atribuído em: ${new Date(ou.created_at).toLocaleDateString()}`}
                                    />
                                    <ListItemSecondaryAction>
                                        <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveUser(ou.user_id)} color="error">
                                            <DeleteIcon />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            );
                        })
                    )}
                </List>
            </Paper>
        </Box>
    );
};
